/**
 * **Raves on the same dates: pick the one you're going to** (the user, 2026-10-30: "if a raver attended 4 raves at
 * the same dates, that's not a problem in the app, it's a problem with the raver — put a message that they can't be
 * at two raves at once, they need to choose which one they go to, otherwise it will not work offline"; "and after
 * they chose, unattend the ones they are not going to"). The iPhone app keeps three raves for offline, soonest first
 * (`offline/raveSlots.ts`); raves that overlap are the raver's to settle, not the app's to guess.
 *
 * Signed in, from the SERVER's list of My raves: the first group of attended raves, not over yet, whose dates overlap.
 * Each "I'm going to …" leaves the others (`DELETE /editions/:id/attend`, as My raves' own Leave does — widgets
 * included); "Not now" asks again a day later, or at once when the group changes.
 * The words are the user's (2026-10-31, "Choose your next adventure ✨"); each rave is its LOGO, else its name, then the
 * edition — as everywhere (`RaveMenuName`), the edition in the button's own colour, as the logo (2026-11-02: "barely
 * visible — black, same as the logos").
 *
 * **Chosen from inside ANOTHER rave of the group** (the user, 2026-11-02: "I was in Tomorrowland and chose OZORA, and it
 * kept me in Tomorrowland"): it asks "You're in A and you chose B — is that right?"; yes leaves the others, gives back
 * every other rave's offline copy on this device (this address's worker here, the rest by `releaseThen`'s hops) and
 * lands in B; no goes back to the choice.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Dialog, useToast } from '@/ui';
import { useT } from '@/i18n';
import { endpoints, type MyFestival } from '@/lib/api';
import { clearWidgets } from '@/lib/nativeBridge';
import { readJson, writeJson } from '@/lib/storage';
import { isFramed } from '@/lib/framed';
import { editionSlugFromPath, festivalUrl, resolveTenant } from '@/lib/tenant';
import { dropWorker } from '@/offline/register';
import { releaseThen } from '@/offline/raveSlots';
import { useAuth } from './AuthProvider';
import { RaveMenuName } from './RaveMenuName';
import { useAppUpdateSettled } from './AppUpdate';
import './SameDates.css';

export const SAME_DATES_SNOOZE_MS = 24 * 60 * 60_000;
const LATER = 'same-dates-later';

/** The first group of attended raves, not over yet, whose dates overlap — two or more, soonest first. */
export function sameDates(festivals: readonly MyFestival[], now = Date.now()): MyFestival[] {
  const at = (s: string | null) => (s ? Date.parse(s) : NaN);
  const open = festivals
    .filter((f) => f.attending !== false && Number.isFinite(at(f.gatesOpenAt)) && Number.isFinite(at(f.endsAt)) && at(f.endsAt) > now)
    .sort((a, b) => at(a.gatesOpenAt) - at(b.gatesOpenAt) || (a.editionId < b.editionId ? -1 : 1));
  let group: MyFestival[] = [];
  let end = -Infinity;
  for (const f of open) {
    if (group.length && at(f.gatesOpenAt) < end) { group.push(f); end = Math.max(end, at(f.endsAt)); continue; }
    if (group.length >= 2) return group;
    group = [f];
    end = at(f.endsAt);
  }
  return group.length >= 2 ? group : [];
}

const groupKey = (g: readonly MyFestival[]) => g.map((f) => f.editionId).sort().join(',');

/**
 * The rave of `group` this page stands in: its address, and — when two of its years are in the group — the year the
 * path names. Null on the main app, on another rave, or when the year cannot be told.
 */
export function standingIn(group: readonly MyFestival[], subdomain: string | null, pathname: string): MyFestival | null {
  if (!subdomain) return null;
  const here = group.filter((f) => f.subdomain === subdomain);
  if (here.length <= 1) return here[0] ?? null;
  const slug = editionSlugFromPath(here.map((f) => f.editionSlug), pathname);
  return here.find((f) => f.editionSlug === slug) ?? null;
}

/** Where a switch goes: through every OTHER rave address of the group (each gives its copy back), into the chosen. */
export function switchUrl(group: readonly MyFestival[], going: MyFestival, current: string | null): string {
  const others = [...new Set(group.map((f) => f.subdomain))].filter((s) => s !== going.subdomain && s !== current).sort();
  return releaseThen(others, festivalUrl(going.subdomain, going.editionSlug));
}

export function SameDates() {
  const tr = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const mine = useQuery({ queryKey: ['me', 'festivals'], queryFn: endpoints.myFestivals, enabled: Boolean(user) && !isFramed(), staleTime: 5 * 60_000 });
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0);
  /** Chosen from inside another rave: waiting for "is that right?". */
  const [asking, setAsking] = useState<MyFestival | null>(null);
  // The phone app's update sheet comes first (the 0.14.82 walk); this waits for it.
  const updateSettled = useAppUpdateSettled();
  const group = mine.data && !mine.data.fromDevice ? sameDates(mine.data.festivals) : [];
  if (group.length < 2 || !updateSettled) return null;
  const later = readJson<{ key?: string; until?: number } | null>(null, LATER, null);
  if (later && later.key === groupKey(group) && (later.until ?? 0) > Date.now()) return null;

  const subdomain = resolveTenant().subdomain;
  const here = standingIn(group, subdomain, window.location.pathname);
  const snooze = () => { writeJson(null, LATER, { key: groupKey(group), until: Date.now() + SAME_DATES_SNOOZE_MS }); setTick((n) => n + 1); };
  const leaveOthers = async (going: MyFestival): Promise<boolean> => {
    setBusy(true);
    try {
      for (const f of group) {
        if (f.editionId === going.editionId) continue;
        await endpoints.unattend(f.editionId);
        clearWidgets(f.editionId);
      }
      return true;
    } catch {
      toast.show(tr('That did not go through. Try again when you have a signal.'), { tone: 'error' });
      return false;
    } finally {
      await queryClient.invalidateQueries({ queryKey: ['me', 'festivals'] });
      setBusy(false);
    }
  };
  const choose = (going: MyFestival) => {
    // Standing in another rave of the group: ask first (2026-11-02).
    if (here && here.editionId !== going.editionId) { setAsking(going); return; }
    void leaveOthers(going);
  };
  const switchTo = async (going: MyFestival) => {
    if (!(await leaveOthers(going))) return;
    setBusy(true);
    // This address's own copy, here (a page can free only its own); the others on the way to the chosen rave.
    if (subdomain && subdomain !== going.subdomain && typeof navigator !== 'undefined' && 'serviceWorker' in navigator) await dropWorker();
    window.location.replace(switchUrl(group, going, subdomain));
  };
  const nameOf = (f: MyFestival) => <RaveMenuName festivalName={f.festivalName} editionName={f.editionName} logoUrl={f.logoUrl} />;
  if (asking && here) {
    return (
      <Dialog
        title={tr('Switch raves?')} onClose={() => setAsking(null)} centered
        footer={<>
          <Button variant="quiet" onClick={() => setAsking(null)} disabled={busy}>{tr('No, go back')}</Button>
          <Button onClick={() => { void switchTo(asking); }} disabled={busy}>{tr('Yes, switch')}</Button>
        </>}
      >
        <div className="same-dates">
          <p className="same-dates-switch">
            {tr("You're in {here}, and you chose {going}. Is that right?", { here: here.editionName, going: asking.editionName })}
          </p>
          <div className="same-dates-pair" aria-hidden="true">
            <span className="same-dates-chip">{nameOf(here)}</span>
            <span className="same-dates-arrow">→</span>
            <span className="same-dates-chip">{nameOf(asking)}</span>
          </div>
          <p>{tr("Yes takes you to {going} and removes the other raves' offline copies from this device.", { going: asking.editionName })}</p>
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog title={tr('Choose your next adventure ✨')} onClose={snooze} centered footer={<Button variant="quiet" onClick={snooze} disabled={busy}>{tr('Not now')}</Button>}>
      <div className="same-dates">
      <p className="same-dates-hey">{tr('Whoa, easy there, party animal! 🐒')}</p>
      <p>
        {tr("You've joined {count} raves on the same dates.", { count: group.length })}<br />
        {tr("We love your energy, but teleportation isn't available... yet! 😉")}
      </p>
      <p>
        {tr("Pick the rave you're heading to.")}<br />
        {tr('The others will leave My Raves.')}<br />
        {tr('Your chosen rave stays in your pocket, even offline.')}<br />
        {tr('Now go find your dancefloor! 🔊')}
      </p>
      <div className="same-dates-raves">
        {group.map((f) => (
          <Button key={f.editionId} full disabled={busy} onClick={() => choose(f)}>
            <span aria-hidden="true">{nameOf(f)}</span>
            <span className="ui-sr-only">{tr("I'm going to {name}", { name: f.editionName })}</span>
          </Button>
        ))}
      </div>
      </div>
    </Dialog>
  );
}
